import { useState, useCallback, useRef, useEffect } from 'preact/hooks'
import { assetApi } from '@/api/asset'
import type { WebAssetViewDto, AssetVersionViewDto, AgentEffort } from '@/api/asset'
import { useToast } from '@/hooks/useToast'

type WebsterAssetType = 'Landing Page' | 'Email Template' | 'Email Copy'

interface UseWebsterBuilderOptions {
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    assetId: string
    assetType: WebsterAssetType
    onAssetUpdated?: () => void
}

/**
 * Shared orchestrator hook for the Webster builder overlay.
 *
 * Manages the asset detail state, version promotion (Save as Version) and the asset's
 * generation effort level.
 * The type-specific chat hooks (useLandingPageChat / useEmailAssetChat) are
 * composed by the overlay component itself since they need to integrate with
 * the UI (preview panels, editors, etc.).
 */
export function useWebsterBuilder({
    clientCompanyId,
    workspaceId,
    campaignId,
    assetId,
    assetType,
    onAssetUpdated,
}: UseWebsterBuilderOptions) {
    const [assetDetail, setAssetDetail] = useState<WebAssetViewDto | null>(null)
    const [versions, setVersions] = useState<AssetVersionViewDto[]>([])
    const [loading, setLoading] = useState(true)
    const [promoting, setPromoting] = useState(false)
    // Shown while the save is in flight. Clearing it re-renders the picker, so a failed save puts the
    // select back on the stored level instead of leaving it on the one that was refused.
    const [pendingGenerationEffort, setPendingGenerationEffort] = useState<AgentEffort | null>(null)
    const { showSuccess, showError, showErrorUnless422 } = useToast()
    const onAssetUpdatedRef = useRef(onAssetUpdated)
    onAssetUpdatedRef.current = onAssetUpdated

    const loadAssetData = useCallback(async (): Promise<WebAssetViewDto | null> => {
        try {
            const [detailRes, versionsRes] = await Promise.all([
                assetApi.getAssetDetail(clientCompanyId, workspaceId, campaignId, assetId),
                assetApi.getAssetVersions(clientCompanyId, workspaceId, assetId),
            ])
            if (!detailRes.error && detailRes.data) setAssetDetail(detailRes.data)
            if (!versionsRes.error && versionsRes.data) setVersions(versionsRes.data)
            return detailRes.data ?? null
        } finally {
            setLoading(false)
        }
    }, [clientCompanyId, workspaceId, campaignId, assetId])

    useEffect(() => { loadAssetData() }, [loadAssetData])

    const hasLandingPage = assetType === 'Landing Page' && !!assetDetail?.LandingPageId

    const promoteDraft = useCallback(async (options?: {
        versionComment?: string
    }) => {
        setPromoting(true)
        try {
            const response = await assetApi.promoteDraft(
                clientCompanyId, workspaceId, campaignId, assetId, options
            )
            if (response.error) {
                showErrorUnless422('Save failed', response)
                return false
            }
            showSuccess('Version created', 'Saved as a new version for review.')
            await loadAssetData()
            onAssetUpdatedRef.current?.()
            return true
        } catch {
            showError('Save failed', 'An unexpected error occurred.')
            return false
        } finally {
            setPromoting(false)
        }
    }, [clientCompanyId, workspaceId, campaignId, assetId, loadAssetData, showSuccess, showError])

    const updateGenerationEffort = useCallback(async (effort: AgentEffort) => {
        setPendingGenerationEffort(effort)
        try {
            const response = await assetApi.updateAsset(clientCompanyId, workspaceId, campaignId, assetId, {
                Id: assetId,
                CampaignId: campaignId,
                GenerationEffort: effort,
            })
            if (response.error) {
                showErrorUnless422('Effort not changed', response)
                return
            }
        } catch {
            showError('Effort not changed', 'An unexpected error occurred.')
            return
        } finally {
            setPendingGenerationEffort(null)
        }
        // Saved. Show it before the reload, so a reload that fails cannot put the picker back on a
        // level the server no longer holds — the next build would run at one the user can't see.
        setAssetDetail(previous => previous ? { ...previous, GenerationEffort: effort } : previous)
        await loadAssetData().catch(() => undefined)
    }, [clientCompanyId, workspaceId, campaignId, assetId, loadAssetData, showError, showErrorUnless422])

    return {
        assetDetail,
        versions,
        loading,
        hasLandingPage,
        promoting,
        promoteDraft,
        refreshAsset: loadAssetData,
        generationEffort: pendingGenerationEffort ?? assetDetail?.GenerationEffort ?? null,
        savingGenerationEffort: pendingGenerationEffort !== null,
        updateGenerationEffort,
    }
}

export type { WebsterAssetType }
